import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import React, { useCallback, useEffect } from 'react';

import { ReactComponent as CheckCircle } from '@pflegia/ui/icons/check-circle.svg';
import { ReactComponent as TimesCircle } from '@pflegia/ui/icons/times-circle.svg';

import { ToastMessageType } from './ToastProvider';

export interface ToastMessageProps {
  id: string;
  lifetime?: number;
  variant?: ToastMessageType;
  onRemove?: (id: string) => void;
  message: string;
}

const ToastMessage: React.FC<ToastMessageProps> = ({ id, message, lifetime, onRemove, variant }) => {
  const { t } = useTranslation('common');

  useEffect(() => {
    if (lifetime && onRemove) {
      const timeout = setTimeout(() => {
        onRemove(id);
      }, lifetime);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [id, lifetime, onRemove]);

  const handleRemove = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    onRemove?.(id);
  }, [id, onRemove]);

  return (
    <div
      className={clsx(
        'visible flex w-full flex-row rounded-lg shadow-lg',
        'transform text-white transition-all hover:scale-102',
        variant && 'max-h-40',
        variant === ToastMessageType.Info && 'bg-main-blue',
        variant === ToastMessageType.Error && 'bg-declined',
        variant === ToastMessageType.Success && 'bg-success',
        variant === ToastMessageType.Warning && 'bg-orange-500',
      )}
      role='alert'>
      {variant === ToastMessageType.Success && <CheckCircle className='mx-4 my-auto shrink-0 text-white' />}
      {variant === ToastMessageType.Error && <TimesCircle className='mx-4 my-auto shrink-0 text-white' />}
      <div className='flex w-full flex-row p-2'>
        <div className='flex w-full flex-col px-1'>
          <div className='my-auto flex text-lg font-semibold select-none'>{t(`toastMessageType.${variant}`)}</div>
          <p className='my-auto -mt-0.5 flex text-sm break-words text-white'>{message}</p>
        </div>
        <button
          aria-label={t('close')}
          onClick={handleRemove}
          className='mx-auto h-12 w-5 cursor-pointer items-end text-center text-xs leading-none'>
          X
        </button>
      </div>
    </div>
  );
};

export default ToastMessage;
